package com.jingfangpai.web.components

import antd.button.button
import antd.card.card
import antd.icon.deleteOutlined
import antd.icon.editOutlined
import antd.icon.plusOutlined
import antd.message.message
import antd.modal.ModalComponent
import antd.modal.confirm
import com.jingfangpai.api.clients.FileApiClient
import com.jingfangpai.common.data.type.Date
import com.jingfangpai.common.orm.mapping.File
import com.jingfangpai.common.orm.mapping.enums.FileEntityType
import com.jingfangpai.common.orm.mapping.enums.FileType
import kotlinext.js.Object
import kotlinext.js.js
import kotlinext.js.jsObject
import kotlinx.browser.window
import kotlinx.html.InputType
import kotlinx.html.js.onChangeFunction
import kotlinx.html.js.onClickFunction
import kotlinx.html.onClick
import kotlinx.html.style
import org.w3c.dom.HTMLInputElement
import org.w3c.files.FileReader
import react.*
import react.dom.div
import react.dom.h3
import react.dom.img
import react.dom.input
import react.dom.jsStyle
import urlBase

external interface ImageManagerProps : RProps {
    var entityType: FileEntityType
    var entityId: String
    var onImagesChanged: ((Array<File>) -> Unit)?
}

data class ImageWithStatus(val file: File, val needsUpload: Boolean)

fun Boolean.toInt() = if (this) 1 else 0

val ImageManager = functionalComponent<ImageManagerProps> { props ->
    val (images, setImages) = useState(arrayOf<ImageWithStatus>())
    val (previewVisible, setPreviewVisible) = useState(false)
    val (previewImage, setPreviewImage) = useState("")
    val (previewTitle, setPreviewTitle) = useState("")
    val (previewingFile, setPreviewingFile) = useState<File?>(null)
    val (uploading, setUploading) = useState(false)
    val (loading, setLoading) = useState(false)
    val (urlUploadVisible, setUrlUploadVisible) = useState(false)
    val (imageUrl, setImageUrl) = useState("")
    val (urlPreviewError, setUrlPreviewError) = useState(false)
    val fileInputRef = useRef<HTMLInputElement>(null)

    fun loadExistingImages() {
        setLoading(true)
        FileApiClient.getByEntityAndType(
            entityType = props.entityType,
            entityId = props.entityId,
            type = FileType.图片,
            okCallback = { response ->
                setLoading(false)
                if (response.ok() && response.data != null) {
                    setImages(response.data!!.map { ImageWithStatus(it, false) }.toTypedArray())
                }
            },
            errorCallback = { error ->
                setLoading(false)
                console.error("Failed to load images:", error)
            }
        )
    }

    fun uploadImage(file: File) {
        setUploading(true)

        FileApiClient.upload(
            file = file,
            okCallback = { response ->
                if (response.ok() && response.data != null) {
                    val uploadedFile = response.data!!
                    // Reload images from server to ensure consistency
                    loadExistingImages()
                    setUploading(false)
                    message.success("图片上传成功")
                    props.onImagesChanged?.invoke(images.map { it.file }.toTypedArray())
                    
                    // Close URL upload modal if it's open
                    if (urlUploadVisible) {
                        setUrlUploadVisible(false)
                        setImageUrl("")
                        setUrlPreviewError(false)
                    }
                } else {
                    setUploading(false)
                    message.error("图片上传失败: ${response.message}")
                }
            },
            errorCallback = { error ->
                setUploading(false)
                message.error("图片上传失败: ${error.message}")
            }
        )
    }

    fun handlePaste(event: dynamic) {
        event.preventDefault()
        val clipboardData = event.clipboardData ?: event.originalEvent?.clipboardData
        
        if (clipboardData == null) {
            message.warning("无法访问剪贴板")
            return
        }
        
        val items = clipboardData.items
        if (items == null || items.length == 0) {
            message.warning("剪贴板中没有内容")
            return
        }
        
        var imageFound = false
        for (i in 0 until items.length) {
            val item = items[i]
            if (item.type.indexOf("image") != -1) {
                imageFound = true
                val blob = item.getAsFile()
                if (blob != null) {
                    val reader = FileReader()
                    reader.onload = { e: dynamic ->
                        val base64String = e.target.result as String
                        // Remove data:image/xxx;base64, prefix
                        val cleanBase64 = base64String.substringAfter(",")
                        val trackingId = "paste_${Date().getTime()}"
                        
                        val newFile = File().apply {
                            this.base64 = cleanBase64
                            this.name = "pasted_image.png"
                            this.entityType = props.entityType
                            this.entityId = props.entityId
                            this.type = FileType.图片
                            this.trackingId = trackingId
                        }
                        
                        // Use functional update to ensure we append to current state
                        setImages { currentImages ->
                            currentImages + ImageWithStatus(newFile, true)
                        }
                        uploadImage(newFile)
                        message.success("图片已粘贴，正在上传...")
                        
                        // Close URL upload modal if it's open
                        if (urlUploadVisible) {
                            setUrlUploadVisible(false)
                            setImageUrl("")
                            setUrlPreviewError(false)
                        }
                    }
                    reader.readAsDataURL(blob)
                }
                break
            }
        }
        
        if (!imageFound) {
            message.warning("剪贴板中没有图片")
        }
    }

    useEffect(props.entityId, urlUploadVisible) {
        if (!props.entityId.isNullOrBlank()) {
            loadExistingImages()
        }
        
        // Add global paste event listener only when URL upload modal is visible
        val pasteHandler: (dynamic) -> Unit = { event ->
            if (urlUploadVisible) {
                handlePaste(event)
            }
        }
        
        if (urlUploadVisible) {
            kotlinx.browser.document.addEventListener("paste", pasteHandler)
        }
        
        // Cleanup: remove event listener on unmount or when urlUploadVisible changes
        cleanup {
            kotlinx.browser.document.removeEventListener("paste", pasteHandler)
        }
    }

    fun handleImageSelect(event: dynamic) {
        val input = event.target as HTMLInputElement
        val files = input.files
        
        if (files != null && files.length > 0) {
            val file = files.item(0)
            if (file != null) {
                val reader = FileReader()
                
                reader.onload = { e: dynamic ->
                    val base64String = e.target.result as String
                    // Remove data:image/xxx;base64, prefix
                    val cleanBase64 = base64String.substringAfter(",")
                    val trackingId = "local_${Date().getTime()}"
                    
                    val newFile = File().apply {
                        this.base64 = cleanBase64
                        this.name = file.name
                        this.entityType = props.entityType
                        this.entityId = props.entityId
                        this.type = FileType.图片
                        this.trackingId = trackingId
                    }
                    
                    // Use functional update to ensure we append to current state
                    setImages { currentImages ->
                        currentImages + ImageWithStatus(newFile, true)
                    }
                    uploadImage(newFile)
                }
                
                reader.readAsDataURL(file)
            }
        }
        
        input.value = ""
    }

    fun handleDeleteImage(index: Int) {
        val imageToDelete = images[index]
        
        confirm(jsObject {
            title = "确认删除"
            content = "确定要删除这张图片吗？"
            okText = "删除"
            cancelText = "取消"
            onOk = {
                if (imageToDelete.file.id != null && !imageToDelete.file.id!!.isBlank()) {
                    FileApiClient.removeByIdAndName(
                        id = imageToDelete.file.id!!,
                        name = imageToDelete.file.name ?: "",
                        okCallback = { response ->
                            if (response.ok()) {
                                setImages(images.filterIndexed { i, _ -> i != index }.toTypedArray())
                                props.onImagesChanged?.invoke(images.filterIndexed { i, _ -> i != index }.map { it.file }.toTypedArray())
                                message.success("图片已删除")
                            } else {
                                message.error("删除失败: ${response.message}")
                            }
                        },
                        errorCallback = { error ->
                            message.error("删除失败: ${error.message}")
                        }
                    )
                } else {
                    setImages(images.filterIndexed { i, _ -> i != index }.toTypedArray())
                    props.onImagesChanged?.invoke(images.filterIndexed { i, _ -> i != index }.map { it.file }.toTypedArray())
                }
            }
        })
    }

    fun handlePreview(image: ImageWithStatus) {
        val previewSrc = if (!image.file.base64.isNullOrBlank()) {
            if (image.file.base64!!.startsWith("data:")) {
                image.file.base64
            } else {
                "data:image/png;base64,${image.file.base64}"
            }
        } else {
            image.file.url ?: ""
        }
        setPreviewImage(previewSrc?:"")
        setPreviewTitle(image.file.name ?: "图片预览")
        setPreviewingFile(image.file)
        setPreviewVisible(true)
    }

    fun handleDeleteFromPreview() {
        val fileToDelete = previewingFile
        if (fileToDelete == null) {
            setPreviewVisible(false)
            return
        }

        ModalComponent.confirm(jsObject {
            title = "确认删除"
            content = "确定要删除这张图片吗？"
            okText = "删除"
            cancelText = "取消"
            onOk = { _: dynamic ->
                if (!fileToDelete.id.isNullOrBlank()) {
                    FileApiClient.removeByIdAndName(
                        id = fileToDelete.id!!,
                        name = fileToDelete.name ?: "",
                        okCallback = { response ->
                            if (response.ok()) {
                                // Remove from images array
                                setImages(images.filter { it.file.trackingId != fileToDelete.trackingId }.toTypedArray())
                                props.onImagesChanged?.invoke(images.filter { it.file.trackingId != fileToDelete.trackingId }.map { it.file }.toTypedArray())
                                message.success("图片已删除")
                                ModalComponent.destroyAll()
                                setPreviewVisible(false)
                                setPreviewingFile(null)
                            } else {
                                message.error("删除失败: ${response.message}")
                            }
                        },
                        errorCallback = { error ->
                            message.error("删除失败: ${error.message}")
                        }
                    )
                } else {
                    // Local file not yet uploaded
                    setImages(images.filter { it.file.trackingId != fileToDelete.trackingId }.toTypedArray())
                    props.onImagesChanged?.invoke(images.filter { it.file.trackingId != fileToDelete.trackingId }.map { it.file }.toTypedArray())
                    message.success("图片已删除")
                    ModalComponent.destroyAll()
                    setPreviewVisible(false)
                    setPreviewingFile(null)
                }
            }
        })
    }

    fun triggerFileSelect() {
        fileInputRef.current?.click()
    }

    fun handleUrlUpload() {
        if (imageUrl.isBlank()) {
            message.warning("请输入图片链接")
            return
        }

        setUploading(true)
        
        // Create an image element to load and convert to base64
        val img = org.w3c.dom.Image()
        img.crossOrigin = "anonymous"
        
        img.onload = { _: dynamic ->
            try {
                // Create canvas to convert image to base64
                val canvas = kotlinx.browser.document.createElement("canvas") as org.w3c.dom.HTMLCanvasElement
                canvas.width = img.width
                canvas.height = img.height
                
                val ctx = canvas.getContext("2d") as org.w3c.dom.CanvasRenderingContext2D
                ctx.drawImage(img.asDynamic(), 0.0, 0.0, img.width.toDouble(), img.height.toDouble())
                
                val base64String = canvas.toDataURL("image/png")
                // Remove data:image/png;base64, prefix
                val cleanBase64 = base64String.substringAfter(",")
                val trackingId = "url_${Date().getTime()}"
                
                val newFile = File().apply {
                    this.base64 = cleanBase64
                    this.name = "image_from_url.png"
                    this.entityType = props.entityType
                    this.entityId = props.entityId
                    this.type = FileType.图片
                    this.trackingId = trackingId
                    this.url = imageUrl
                }
                
                setImages { currentImages ->
                    currentImages + ImageWithStatus(newFile, true)
                }
                uploadImage(newFile)
                
                setUrlUploadVisible(false)
                setImageUrl("")
                setUrlPreviewError(false)
                setUploading(false)
                message.success("图片上传成功")
            } catch (e: Exception) {
                console.error("Failed to convert image:", e)
                setUrlPreviewError(true)
                setUploading(false)
                message.error("图片转换失败，请检查链接是否有效")
            }
        }
        
        img.onerror = { _: dynamic, _: String, _: Int, _: Int, _: Any? ->
            console.error("Failed to load image from URL:", imageUrl)
            setUrlPreviewError(true)
            setUploading(false)
            message.error("无法加载图片，请检查链接是否正确")
        }
        
        img.src = imageUrl
    }

    

    div("image-manager") {
        attrs.jsStyle = js {
            marginBottom = "16px"
        }

        card {
            attrs.title = "图片"
            attrs.extra = arrayOf(
                buildElement {
                    button {
                        attrs.type = "default"
                        attrs.style = js { marginRight = "8px" }
                        attrs.onClick = {
                            setUrlUploadVisible(true)
                            setImageUrl("")
                            setUrlPreviewError(false)
                        }
                        attrs.disabled = uploading
                        +"快捷上传"
                    }
                },
                buildElement {
                    button {
                        attrs.type = "primary"
                        attrs.ghost = true
                        attrs.icon = buildElement {
                            plusOutlined {}
                        }
                        attrs.onClick = {
                            triggerFileSelect()
                        }
                        attrs.disabled = uploading
                        +"上传图片"
                    }
                }
            )


            input {
                ref = fileInputRef
                attrs.type = InputType.file
                attrs["accept"] = "image/*"
                attrs.jsStyle = js { display = "none" }
                attrs.onChangeFunction = { event: dynamic ->
                    handleImageSelect(event)
                }
            }

            if (loading) {
                div {
                    attrs.jsStyle = js {
                        textAlign = "center"
                        padding = "40px 0"
                        color = "#999"
                    }
                    +"加载中..."
                }
            } else if (images.isEmpty()) {
                div {
                    attrs.jsStyle = js {
                        textAlign = "center"
                        padding = "40px 0"
                        color = "#999"
                    }
                    +"暂无图片，点击上方按钮上传"
                }
            } else {
                div {
                    attrs.jsStyle = js {
                        display = "flex"
                        flexWrap = "wrap"
                        gap = "12px"
                    }

                    images.forEachIndexed { index, imageWithStatus ->
                        div {
                            attrs.jsStyle = js {
                                position = "relative"
                                width = "120px"
                                height = "120px"
                                border = "1px solid #d9d9d9"
                                borderRadius = "4px"
                                overflow = "hidden"
                            }

                            img {
                                val imageSrc = if (!imageWithStatus.file.base64.isNullOrBlank()) {
                                    if (imageWithStatus.file.base64!!.startsWith("data:")) {
                                        imageWithStatus.file.base64
                                    } else {
                                        "data:image/png;base64,${imageWithStatus.file.base64}"
                                    }
                                } else {
                                    imageWithStatus.file.url ?: ""
                                }
                                attrs.src = imageSrc?:""
                                attrs.alt = imageWithStatus.file.name ?: "图片"
                                attrs.jsStyle = js {
                                    width = "100%"
                                    height = "100%"
                                    objectFit = "cover"
                                    cursor = "pointer"
                                }
                                attrs.onClickFunction = {
                                    handlePreview(imageWithStatus)
                                }
                            }

                            if (imageWithStatus.needsUpload) {
                                div {
                                    attrs.jsStyle = js {
                                        position = "absolute"
                                        top = "0"
                                        left = "0"
                                        right = "0"
                                        bottom = "0"
                                        backgroundColor = "rgba(0, 0, 0, 0.5)"
                                        display = "flex"
                                        alignItems = "center"
                                        justifyContent = "center"
                                        color = "white"
                                        fontSize = "12px"
                                    }
                                    +"上传中..."
                                }
                            }

                            div {
                                attrs.jsStyle = js {
                                    position = "absolute"
                                    top = "4px"
                                    right = "4px"
                                    display = "flex"
                                    gap = "4px"
                                    opacity = "0"
                                    transition = "opacity 0.2s"
                                }
                                attrs.asDynamic().onMouseEnter = { e: dynamic ->
                                    val target = e.currentTarget.asDynamic()
                                    target.style.opacity = "1"
                                }
                                attrs.asDynamic().onMouseLeave = { e: dynamic ->
                                    val target = e.currentTarget.asDynamic()
                                    target.style.opacity = "0"
                                }

                                if (imageWithStatus.file.id.isNullOrBlank()) {
                                    button {
                                        attrs.type = "link"
                                        attrs.size = "small"
                                        attrs.className = "image-action-btn"
                                        attrs.onClick = {
                                            message.info("编辑功能开发中")
                                        }
                                        attrs.icon = buildElement {
                                            editOutlined {}
                                        }
                                    }
                                }

                                button {
                                    attrs.type = "link"
                                    attrs.size = "small"
                                    attrs.ghost = true
                                    attrs.className = "image-action-btn"
                                    attrs.onClick = {
                                        handleDeleteImage(index)
                                    }
                                    attrs.icon = buildElement {
                                        deleteOutlined {}
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // URL Upload Modal
        if (urlUploadVisible) {
            div {
                attrs.jsStyle = js {
                    position = "fixed"
                    top = "0"
                    left = "0"
                    right = "0"
                    bottom = "0"
                    backgroundColor = "rgba(0, 0, 0, 0.45)"
                    zIndex = "1000"
                    display = "flex"
                    alignItems = "center"
                    justifyContent = "center"
                }
                attrs.onClickFunction = {
                    if (!uploading) {
                        setUrlUploadVisible(false)
                        setImageUrl("")
                        setUrlPreviewError(false)
                    }
                }

                div {
                    attrs.jsStyle = js {
                        backgroundColor = "white"
                        borderRadius = "8px"
                        padding = "24px"
                        maxWidth = "600px"
                        width = "90%"
                        maxHeight = "80vh"
                        overflow = "auto"
                    }
                    attrs.onClickFunction = { event ->
                        event.stopPropagation()
                    }

                    h3 {
                        attrs.jsStyle = js {
                            marginTop = "0"
                            marginBottom = "16px"
                        }
                        +"快捷上传图片"
                    }

                    div {
                        attrs.jsStyle = js {
                            marginBottom = "16px"
                            color = "#666"
                            fontSize = "14px"
                        }
                        +"方式1：直接粘贴图片（Ctrl+V / Cmd+V）"
                    }

                    div {
                        attrs.jsStyle = js {
                            marginBottom = "16px"
                            color = "#666"
                            fontSize = "14px"
                        }
                        +"方式2：输入图片URL地址"
                    }

                    input {
                        attrs.type = InputType.text
                        attrs.placeholder = "https://example.com/image.png"
                        attrs.value = imageUrl
                        attrs.jsStyle = js {
                            width = "100%"
                            padding = "8px 12px"
                            border = "1px solid #d9d9d9"
                            borderRadius = "4px"
                            marginBottom = urlPreviewError.toInt() * 8
                        }
                        attrs.onChangeFunction = { e: dynamic ->
                            setImageUrl(e.target.value as String)
                            setUrlPreviewError(false)
                        }
                    }

                    if (urlPreviewError) {
                        div {
                            attrs.jsStyle = js {
                                color = "#ff4d4f"
                                fontSize = "12px"
                                marginTop = "4px"
                                marginBottom = "8px"
                            }
                            +"图片加载失败，请检查链接是否正确"
                        }
                    }

                    // Preview
                    if (!imageUrl.isBlank()) {
                        div {
                            attrs.jsStyle = js {
                                marginTop = "16px"
                                marginBottom = "16px"
                                border = "1px solid #d9d9d9"
                                borderRadius = "4px"
                                padding = "8px"
                                textAlign = "center"
                                minHeight = "100px"
                                display = "flex"
                                alignItems = "center"
                                justifyContent = "center"
                            }

                            img {
                                attrs.src = imageUrl
                                attrs.alt = "预览"
                                attrs.jsStyle = js {
                                    maxWidth = "100%"
                                    maxHeight = "300px"
                                    objectFit = "contain"
                                }
                                attrs["onError"] = {
                                    setUrlPreviewError(true)
                                }
                            }
                        }
                    }

                    // Buttons
                    div {
                        attrs.jsStyle = js {
                            display = "flex"
                            justifyContent = "flex-end"
                            gap = "8px"
                            marginTop = "16px"
                        }

                        button {
                            attrs.type = "default"
                            attrs.disabled = uploading
                            attrs.onClick = {
                                setUrlUploadVisible(false)
                                setImageUrl("")
                                setUrlPreviewError(false)
                            }
                            +"取消"
                        }

                        button {
                            attrs.type = "primary"
                            attrs.disabled = uploading || imageUrl.isBlank()
                            attrs.onClick = {
                                handleUrlUpload()
                            }
                            if (uploading) {
                                +"上传中..."
                            } else {
                                +"确定上传"
                            }
                        }
                    }
                }
            }
        }

        if (previewVisible) {
            div {
                attrs.jsStyle = js {
                    position = "fixed"
                    top = "0"
                    left = "0"
                    right = "0"
                    bottom = "0"
                    backgroundColor = "rgba(0, 0, 0, 0.8)"
                    zIndex = "1000"
                    display = "flex"
                    alignItems = "center"
                    justifyContent = "center"
                }
                attrs.onClickFunction = {
                    setPreviewVisible(false)
                    setPreviewingFile(null)
                }

                img {
                    attrs.src = previewImage
                    attrs.alt = previewTitle
                    attrs.jsStyle = js {
                        maxWidth = "90%"
                        maxHeight = "90%"
                        objectFit = "contain"
                    }
                    attrs.onClickFunction = { event ->
                        event.stopPropagation()
                    }
                }

                // Close button
                button {
                    attrs.type = "link"
                    attrs.style = js {
                        position = "absolute"
                        top = "20px"
                        right = "20px"
                        color = "white"
                        fontSize = "32px"
                        padding = "0"
                        lineHeight = "1"
                    }
                    attrs.onClick = { event ->
                        event.stopPropagation()
                        setPreviewVisible(false)
                        setPreviewingFile(null)
                    }
                    +"×"
                }

                // Delete button
                button {
                    attrs.type = "link"
                    attrs.style = js {
                        position = "absolute"
                        bottom = "20px"
                        left = "50%"
                        transform = "translateX(-50%)"
                        color = "white"
                        backgroundColor = "rgba(255, 77, 79, 0.8)"
                        border = "none"
                        borderRadius = "4px"
                        padding = "8px 24px"
                        fontSize = "14px"
                    }
                    attrs.onClick = { event ->
                        event.stopPropagation()
                        handleDeleteFromPreview()
                    }
                    attrs.icon = buildElement {
                        deleteOutlined {}
                    }
                    +"删除图片"
                }
            }
        }
    }
}
